import {captureMessage} from '@sentry/browser';

import {dissoc} from 'ramda';
import {isNilOrEmpty, isNumber} from 'ramda-adjunct';

import {gridDataProtocol} from '../api/gridDataProtocol';
import {RequestCancelledError} from '../api/requestCancelledError';
import type {useHttpCalls} from '../api/useHttpCalls';
import {DEFAULT_SETTINGS} from '../constants/defaultSettings';
import {SMART_SEARCH_KEY} from '../constants/smartSearchKey';
import type {EagGridStore} from '../store/createEagGridStore';
import type {GetDataQueryResponse, QueryIdRequestBody} from '../types/api/data';
import type {QueryFilterObject} from '../types/QueryFilterObject';
import type {RowData} from '../types/RowData';
import {buildActivePreset} from '../utils/buildActivePreset';
import {extractSmartSearch} from '../utils/extractSmartSearch';
import {getSortModel} from '../utils/getSortModel';
import {getVisibleColumnKeys} from '../utils/getVisibleColumnKeys';
import {objectHasEveryEqualPropOfAnother} from '../utils/objectHasEveryEqualPropOfAnother';
import {stripLocalGridSettings} from '../utils/stripLocalGridSettings';
import {unwrapFilterValues} from '../utils/unwrapFilterValues';
import {getSearchParamsByGridCode} from '../utils/url/getSearchParamsByGridCode';
import {setSearchParamsByGridCode} from '../utils/url/setSearchParamsByGridCode';
import {
  EMPTY_SNAPSHOT,
  isPaginationOnly,
  updateSnapshot,
  type FetchStateSnapshot,
} from './fetchStateSnapshot';
import {
  clearPageDataCacheForGrid,
  getCachedPageData,
  getPageDataCacheKey,
  setCachedPageData,
} from './pageDataCache';
import {resolveQueryId} from './resolveQueryId';

type HttpCalls = ReturnType<typeof useHttpCalls>;

type GridDataEngineDeps = {
  store: EagGridStore;
  gridCode: string;
};

/**
 * Per-fetch context shared by getDataByQueryId and its callers: the abort controller plus
 * the page-window (offset/limit), the visible column keys, and the extracted smart-search.
 * All five values are constant for the duration of a single fetchPageInternal invocation.
 */
type FetchPageContext = {
  controller: AbortController;
  offset: number;
  limit: number;
  columns: ReturnType<typeof getVisibleColumnKeys>;
  smartSearch: ReturnType<typeof extractSmartSearch>;
};

/** Per-render inputs pushed in by GridLifecycle (via its React adapter). The engine
 * always reads the LATEST value at call time — same semantics as the previous
 * ref-sync pattern in the old adapter. */
export type GridDataEngineInputs = {
  http: HttpCalls;
  queryModifier?: (filter: QueryFilterObject) => QueryFilterObject;
  predefinedColumns: readonly string[];
};

/**
 * Framework-free server-data engine for one EagGrid mount. Owns queryId
 * resolution (pagination fast-path / preset reuse / fresh createDataQuery),
 * the fetch-state snapshot, the page LRU cache, abort controllers, and
 * response→RowData transformation. Owned by GridLifecycle so race behavior
 * (abort mid-flight, fast paths, cache hits) is unit-testable in isolation.
 */
export class GridDataEngine {
  private snapshot: FetchStateSnapshot = EMPTY_SNAPSHOT;
  private abortController: AbortController | null = null;
  // Delta column fetches run independently of the main fetch and of each other —
  // enabling two columns in quick succession must fill in BOTH. Tracked only to
  // abort on dispose.
  private deltaAbortControllers = new Set<AbortController>();
  private disposed = false;
  // Set by refetchQuery; consumed into a local by the next fetchPageInternal. A dedicated
  // flag (not a nulled snapshot field) is race-proof: a cancelled in-flight fetch's
  // continuation restamps snapshot.queryId in a microtask before the refetch's macrotask
  // resumes, which would re-enable the pagination fast path and skip the /count the
  // refetch must force. The flag is never touched by updateSnapshot, so it survives that race.
  private forceFullRefetch = false;
  // The queryId whose /count actually LANDED (a number reached setTotalCount). Distinct
  // from snapshot.queryId, which records the query the engine last ROUTED to — a fetch
  // cancelled mid-flight stamps that routing even though its count never answered (see
  // getDataByQueryId's cancel path). skipCount is gated on this field so a superseding
  // fetch can never inherit "the count is already up to date" from a query that never
  // produced one. See the skipCount gate in getDataByQueryId (T20-139383).
  private countedQueryId: string | null = null;
  private inputs: GridDataEngineInputs;

  constructor(
    private deps: GridDataEngineDeps,
    initialInputs: GridDataEngineInputs
  ) {
    this.inputs = initialInputs;
  }

  update(inputs: GridDataEngineInputs): void {
    this.inputs = inputs;
  }

  dispose(): void {
    this.disposed = true;
    this.abortController?.abort();
    this.deltaAbortControllers.forEach((controller) => controller.abort());
  }

  /**
   * Clears the disposed flag. Called from the adapter's mount-sync effect so a
   * StrictMode simulated unmount/remount (which reuses the same engine instance)
   * does not leave the engine permanently marked as disposed. Without this,
   * any future disposed-based guard would silently break the grid in dev.
   */
  revive(): void {
    this.disposed = false;
  }

  fetchPage(offset: number): void {
    void this.fetchPageInternal(offset);
  }

  fetchAddedColumns(addedColumns: string[], offset: number): void {
    void this.fetchAddedColumnsInternal(addedColumns, offset);
  }

  /**
   * Full refetch of the CURRENT query (refresh button / refreshDatagrid event / retry).
   * Arms `forceFullRefetch`, then re-runs fetchPage: the next fetch reads the flag into a
   * local that defeats the pagination fast path AND forces skipCount=false — so BOTH /data
   * and /count are refetched from the network for the CURRENT query. Deliberately NOT a
   * full snapshot reset: a null presetJson would force the didPresetChange branch, which
   * routes to the preset's ORIGINAL dataQueryId and would drop a live filter/sort-modified
   * query (the freshly minted id in the URL search param) — reverting the refreshed view to
   * stale data. And deliberately NOT a nulled snapshot.queryId: a fetch cancelled by this
   * refetch restamps snapshot.queryId in a microtask before the refetch's macrotask resumes,
   * so nulling it does not survive the race (the flag does).
   */
  refetchQuery(offset: number): void {
    this.forceFullRefetch = true;
    this.fetchPage(offset);
  }

  /**
   * Retry after a failed fetch. Clears the page cache, then refetches the current
   * query (see refetchQuery). Without the cache clear, a warm entry (e.g. the count
   * failed while the page was cache-served) would pair up-to-60s-old rows with the
   * fresh count.
   */
  retryFetch(offset: number): void {
    clearPageDataCacheForGrid(this.deps.gridCode);
    this.refetchQuery(offset);
  }

  // eslint-disable-next-line max-statements -- Pre-existing violation
  private async fetchPageInternal(fetchOffset: number): Promise<void> {
    // Abort any in-flight request before starting a new one
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;

    // Pre-fetch state for the finally safety net: if rows already exist, reverting
    // means 'success'; before the first successful fetch it means 'idle'.
    const revertState = this.deps.store.getState().rows.length > 0 ? 'success' : 'idle';

    this.deps.store.startFetch();

    try {
      // Yield one macrotask so React commits the loading snapshot before
      // the rest of the fetch runs.  When the page-data cache is warm (or the
      // network is very fast) every subsequent store mutation lands inside the
      // same render batch as the startFetch call, so React never
      // observes the loading state and the LoadingBar's useMinimumLoadingDuration
      // effect never starts its timer — the user sees no loading state at all
      // when switching pages.  setTimeout(0) (~4 ms) is cheap and guarantees a
      // paint between the two state transitions.
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      if (controller.signal.aborted) {
        return;
      }

      const state = this.deps.store.getState();
      const columnStates = state.columnStates;
      const gridSettings = state.gridSettings ?? DEFAULT_SETTINGS;
      const filterValues = state.filterValues ?? {};
      const dataQuery = state.dataQuery;

      if (!dataQuery) {
        this.deps.store.settleFetch('idle');
        return;
      }

      const activePreset = buildActivePreset(
        dataQuery.id,
        columnStates,
        stripLocalGridSettings(gridSettings)
      );

      let searchParams = getSearchParamsByGridCode(this.deps.gridCode);
      if (isNilOrEmpty(searchParams)) {
        setSearchParamsByGridCode(this.deps.gridCode, activePreset.dataQueryId);
        searchParams = getSearchParamsByGridCode(this.deps.gridCode);
      }

      // Sort model derived from current column states (sort lives only in columnStates).
      const sortBy = getSortModel(columnStates);
      const staticFilters = this.inputs.queryModifier ? this.inputs.queryModifier({}) : {};
      const modifiedFilters = this.inputs.queryModifier
        ? this.inputs.queryModifier(filterValues as QueryFilterObject)
        : filterValues;

      const limit = gridSettings.itemsPerPage;

      const smartSearch = extractSmartSearch(modifiedFilters);
      const filtersWithoutSmartSearch = dissoc(SMART_SEARCH_KEY, modifiedFilters);

      const columns = getVisibleColumnKeys(columnStates, this.inputs.predefinedColumns);

      const fetchCtx: FetchPageContext = {
        controller,
        offset: fetchOffset,
        limit,
        columns,
        smartSearch,
      };

      const stateForSnapshot = {
        filterValues: state.filterValues,
        dataQuery,
        columnStates,
        gridSettings: state.gridSettings ?? undefined,
      };

      // Consume the force-full-refetch request (armed by refetchQuery) into a local. Read
      // AFTER the abort check so a refetch superseded before it resumes hands the flag to
      // whichever fetch actually proceeds. When set, treat snapshot.queryId as null for BOTH
      // the fast-path gate and the resolver's skipCount computation — without mutating the
      // snapshot, so a racing cancelled fetch that restamps snapshot.queryId cannot undo it.
      const forceFullRefetch = this.forceFullRefetch;
      this.forceFullRefetch = false;
      const effectiveSnapshotQueryId = forceFullRefetch ? null : this.snapshot.queryId;

      const paginationOnly = !forceFullRefetch && isPaginationOnly(this.snapshot, stateForSnapshot);

      // Fast path: pagination only (offset changed, everything else identical).
      // Skips the expensive JSON.stringify comparison when only the page changed.
      if (paginationOnly) {
        const resolution = resolveQueryId({
          isPaginationOnly: true,
          didPresetChange: false,
          isFilterModified: false,
          isAllStaticFilterPresent: false,
          presetDataQueryId: activePreset.dataQueryId,
          urlQueryId: searchParams?.queryId,
          snapshotQueryId: this.snapshot.queryId,
        });
        // pagination-only always resolves to a reuse; the kind check keeps types narrow.
        if (resolution.kind === 'reuse') {
          await this.getDataByQueryId(resolution.queryId, resolution.skipCount, fetchCtx);
          // paginationOnly: only the page changed. Update queryId only; preserve presetJson &
          // comparisonJson from the prior snapshot so the next non-pagination fetch can still
          // detect a real preset/filter change.
          this.snapshot = updateSnapshot(this.snapshot, stateForSnapshot, {
            queryId: resolution.queryId,
          });
          return;
        }
      }

      const comparison = {smartSearch, filters: filtersWithoutSmartSearch, sortBy};
      const activePresetJson = JSON.stringify(activePreset);
      const comparisonJson = JSON.stringify(comparison);

      // A replaced dataQuery OBJECT is a preset activation/deactivation/reset signal:
      // setDataQuery is only called by usePresetState (activate/deactivate, which reset
      // also routes through) and the cold-mount loader. Identity comparison routes such
      // a fetch to the preset's own dataQueryId — including re-activation of the SAME
      // preset ("modify sort → Reset"), which would otherwise fall through to the
      // isFilterModified branch and mint a fresh dataQueryId, permanently re-dirtying
      // the just-reset preset. See hasPresetChangedSinceSnapshot for why the preset
      // JSON content is NOT compared here.
      const didPresetChange = this.hasPresetChangedSinceSnapshot(this.snapshot, dataQuery);

      const isFilterModified = this.hasFiltersChangedSinceSnapshot(this.snapshot, comparisonJson);

      // Compare staticFilters against the filters actually stored in the existing data-query on the
      // server. This mirrors the old DataGrid's `objectHasEveryEqualPropOfAnother(staticFilters,
      // props.dataQuery.filters)` check.
      //
      // Why not EMPTY_DATA_QUERY_FILTERS ({}): comparing against {} always returns false when
      // staticFilters is non-empty, which caused grids with a queryModifier (mandatory filters like
      // serviceJobId) to skip the createDataQuery call on first render — using the stale queryId
      // that lacked the mandatory filters → 500 from the backend.
      //
      // dataQuery.filters may be an empty array [] (server returns [] for queries with no filters)
      // or a Record. unwrapFilterValues handles both: Object.entries([]) yields nothing → {}.
      const existingQueryFiltersFlat = unwrapFilterValues(
        dataQuery.filters as Record<string, unknown>
      );
      const isAllStaticFilterPresent = objectHasEveryEqualPropOfAnother(
        staticFilters,
        existingQueryFiltersFlat
      );

      // Delegate the queryId + skipCount CHOICE to the pure resolver. The engine still
      // branches on the original booleans below to pick the correct per-branch snapshot
      // field set (resolveQueryId collapses the decision, not the bookkeeping).
      const resolution = resolveQueryId({
        isPaginationOnly: false,
        didPresetChange,
        isFilterModified,
        isAllStaticFilterPresent,
        presetDataQueryId: activePreset.dataQueryId,
        urlQueryId: searchParams?.queryId,
        snapshotQueryId: effectiveSnapshotQueryId,
      });

      // When the preset changed (includes first render), reuse the preset's dataQueryId directly —
      // UNLESS there are mandatory static filters from queryModifier that are missing from the
      // existing data-query. In that case we must create a fresh query with the correct filters.
      if (resolution.kind === 'reuse' && didPresetChange && isAllStaticFilterPresent) {
        await this.getDataByQueryId(resolution.queryId, resolution.skipCount, fetchCtx);
        // didPresetChange branch: refresh presetJson + comparisonJson + queryId. The stored
        // dataQuery object is what lets the NEXT fetch detect re-activation of the same preset
        // (fresh dataQuery object) — the "modify sort -> Reset" regression blocker (commit d82ecd6e02).
        this.snapshot = updateSnapshot(this.snapshot, stateForSnapshot, {
          presetJson: activePresetJson,
          comparisonJson,
          queryId: resolution.queryId,
        });
        return;
      }

      // Filters unchanged and the existing data-query is valid — reuse URL queryId.
      // Skip the /count request when the queryId hasn't changed (pagination only).
      if (resolution.kind === 'reuse' && !isFilterModified && isAllStaticFilterPresent) {
        await this.getDataByQueryId(resolution.queryId, resolution.skipCount, fetchCtx);
        // filters-unchanged branch: update comparisonJson + queryId only (presetJson is
        // preserved as the not-first-fetch marker; preset changes are detected via the
        // dataQuery identity captured in the snapshot by updateSnapshot).
        this.snapshot = updateSnapshot(this.snapshot, stateForSnapshot, {
          comparisonJson,
          queryId: resolution.queryId,
        });
        return;
      }

      const queryIdResponse = await this.inputs.http.createDataQuery(
        {
          filters: unwrapFilterValues(
            filtersWithoutSmartSearch as Record<string, unknown>
          ) as QueryIdRequestBody['filters'],
          order: sortBy,
          smartSearch: smartSearch ?? null,
        },
        controller.signal
      );

      // Cancels reject with RequestCancelledError (handled by the catch below), so
      // an undefined response here is a REAL failure. The guard covers the abort
      // that lands after the response already settled: the result is valid but a
      // newer fetch (or dispose) owns the state — bail before touching it, and
      // before the error check, or a superseded failure settles 'error' over the
      // newer fetch's 'loading' state.
      if (controller.signal.aborted) {
        return;
      }

      if (!queryIdResponse) {
        this.deps.store.settleFetch('error');
        return;
      }

      const newDataQueryId = queryIdResponse.dataQueryId;

      // NOTE: the virtual-preset PUT used to live here (after minting a fresh dataQueryId).
      // It is now owned SOLELY by GridLifecycle.syncDirtyOrPutPreset (Task 5): getDataByQueryId
      // below writes newDataQueryId to the URL search param and then calls store.setQueryId,
      // whose synchronous subscriber notification drives the lifecycle's queryId-change reaction
      // to PUT the virtual preset with this fresh id. Keeping a PUT here too was the documented
      // double-writer race.

      await this.getDataByQueryId(newDataQueryId, false, fetchCtx);
      // create-new branch (fresh dataQuery minted): update all three fields — presetJson,
      // comparisonJson, queryId — the snapshot now fully reflects the new query.
      this.snapshot = updateSnapshot(this.snapshot, stateForSnapshot, {
        presetJson: activePresetJson,
        comparisonJson,
        queryId: newDataQueryId,
      });
    } catch (error) {
      // A canceled request rejects with RequestCancelledError (see handleApiError):
      // not a failure — the superseding fetch or dispose() owns the terminal state
      // (the finally below settles if this call is still the owner). Anything else
      // is unexpected: rethrow to surface it.
      if (!(error instanceof RequestCancelledError)) {
        throw error;
      }
    } finally {
      // Safety net: never leave the machine stuck in 'loading'.
      // - If a NEWER fetchPage aborted us, it replaced this.abortController and owns
      //   the terminal transition — do nothing (owner transfer).
      // - If we were disposed (unmount): dispose() aborts the controller but leaves
      //   this.abortController pointing to it, so the identity check is still true
      //   and we settle here (not the owner-transfer branch).
      // - If an exit path missed its settle (or an unexpected throw), settle back to
      //   the pre-fetch state.
      if (
        this.abortController === controller &&
        this.deps.store.getState().fetchState === 'loading'
      ) {
        this.deps.store.settleFetch(revertState);
      }
    }
  }

  /**
   * Fetches the count (unless skipped) and page data for `resolvedQueryId`, then
   * writes the rows / total count / fetch-state into the store. Extracted from
   * fetchPageInternal; the per-fetch values it used to close over (abort controller,
   * offset, limit, visible columns, smart-search) are passed in via `ctx`.
   *
   * skipCount: when the queryId hasn't changed (pagination only), the total count
   * is the same — skip the redundant /count request to save a network round-trip.
   * It is a ROUTING hint derived from the snapshot, so it is only sound when a count
   * for this exact queryId has already landed — see the `countedQueryId` gate below.
   */
  private async getDataByQueryId(
    resolvedQueryId: string,
    skipCount: boolean,
    ctx: FetchPageContext
  ): Promise<void> {
    // Write the URL search param BEFORE setQueryId. setQueryId notifies the store
    // subscriber SYNCHRONOUSLY, and GridLifecycle's queryId-change reaction is now the
    // SOLE virtual-preset writer — it reads the freshly-minted dataQueryId from the
    // search param. Writing the param first guarantees that reaction sees the NEW id,
    // not the previous one. (Reordering these two is the handoff for the removed engine
    // PUT; see GridLifecycle.syncDirtyOrPutPreset.)
    setSearchParamsByGridCode(this.deps.gridCode, resolvedQueryId);
    this.deps.store.setQueryId(resolvedQueryId);

    // Skipping /count is only safe once THIS query has actually answered one. The
    // snapshot that produced `skipCount` records the query the engine last routed to,
    // and a fetch cancelled mid-flight stamps that routing with its count still
    // unanswered — the superseding fetch then reads "nothing changed since the
    // snapshot", takes the pagination fast path, and would leave `totalCount` at the
    // PREVIOUS query's value: filtered rows under an unfiltered total (a 50-row page
    // showing 21 rows, a paginator offering pages that are empty — T20-139383 /
    // T20-139380). Gating on the landed count costs one /count only in exactly that
    // race; steady-state pagination still skips it.
    const effectiveSkipCount = skipCount && resolvedQueryId === this.countedQueryId;

    const cacheKey = getPageDataCacheKey(
      this.deps.gridCode,
      resolvedQueryId,
      ctx.offset,
      ctx.limit,
      ctx.columns
    );
    const cachedData = getCachedPageData(cacheKey);

    // Fetch count and data in parallel. When only paginating (same queryId),
    // skip the count request entirely — it hasn't changed.
    // When page data is cached, skip the network request entirely.
    let rowCountResult: Awaited<ReturnType<HttpCalls['getCountByQuery']>> | undefined;
    let dataResponse: Awaited<ReturnType<HttpCalls['getDataByQuery']>> | undefined;
    try {
      [rowCountResult, dataResponse] = await Promise.all([
        effectiveSkipCount
          ? Promise.resolve(undefined)
          : this.inputs.http.getCountByQuery(resolvedQueryId, ctx.controller.signal),
        cachedData ??
          this.inputs.http.getDataByQuery(
            resolvedQueryId,
            ctx.offset,
            ctx.limit,
            !!ctx.smartSearch,
            ctx.columns,
            ctx.controller.signal
          ),
      ]);
    } catch (error) {
      // A canceled count/data request rejects with RequestCancelledError. Return
      // NORMALLY (not rethrow): the URL param + setQueryId above already routed to
      // resolvedQueryId, and the caller's snapshot stamp after this return keeps the
      // snapshot consistent with that routing — skipping it would make the next
      // fetch compare against a stale snapshot, see "no filter/sort change", and
      // reuse the superseded queryId (request-races e2e pins this).
      if (error instanceof RequestCancelledError) {
        return;
      }
      throw error;
    }

    // A newer fetchPage call aborted this request — don't overwrite the store
    // with stale/empty results from the cancelled request.
    if (ctx.controller.signal.aborted) {
      return;
    }

    // A failed /count (undefined result when the count was actually requested) is a
    // fetch failure: rendering rows against a stale total would silently lie to the
    // user. Settle to 'error' BEFORE caching the page data so a retry refetches BOTH
    // requests (see retryFetch).
    if (!effectiveSkipCount && !isNumber(rowCountResult)) {
      this.deps.store.settleFetch('error');
      return;
    }

    if (isNumber(rowCountResult)) {
      this.deps.store.setTotalCount(rowCountResult, resolvedQueryId);
      // Only now may a later fetch on this query skip its /count.
      this.countedQueryId = resolvedQueryId;
    }

    if (!dataResponse) {
      this.deps.store.settleFetch('error');
      return;
    }

    if (!cachedData) {
      setCachedPageData(cacheKey, dataResponse);
    }

    try {
      const rowData = gridDataProtocol.normalizeRows(dataResponse);

      this.deps.store.setRows(rowData, {
        queryId: resolvedQueryId,
        offset: ctx.offset,
        limit: ctx.limit,
        hasSmartSearch: Boolean(ctx.smartSearch),
      });
      this.deps.store.settleFetch('success');
    } catch (error) {
      captureMessage('eagGridRowMapping', (scope) =>
        scope.setLevel('error').setExtras({
          userDescription: 'row mapping failed in GridDataEngine',
          error: String(error),
        })
      );
      if (error instanceof Error) {
        console.error(error.message);
      }
      this.deps.store.settleFetch('error');
    }
  }

  /**
   * Fills in newly-shown columns on the current page without reloading the grid.
   *
   * Requests ONLY the added column keys (plus predefined identifiers) for the
   * current data query / page, then merges the returned cells into the existing
   * rows by id. Deliberately does NOT touch the fetch state — the grid keeps showing the
   * current rows; the new column's cells appear when this resolves. While the request is
   * in flight the added keys are exposed via `pendingColumnKeys` so cells can render a
   * loading skeleton (ref-counted, so overlapping deltas for the same column don't
   * strip each other's skeletons).
   *
   * A response for a superseded queryId is dropped (the new query's rows already
   * carry the column). A failed delta falls back to a full fetchPage so the user
   * gets either the data or the standard error state — never silently blank cells.
   */
  private async fetchAddedColumnsInternal(
    addedColumns: string[],
    fetchOffset: number
  ): Promise<void> {
    if (addedColumns.length === 0) {
      return;
    }

    const state = this.deps.store.getState();
    const dataQuery = state.dataQuery;
    const queryId = state.queryId;
    const rows = state.rows;

    // Nothing to augment — let the normal fetchPage path handle loading.
    if (!dataQuery || !queryId || rows.length === 0) {
      return;
    }

    const gridSettings = state.gridSettings ?? DEFAULT_SETTINGS;
    const limit = gridSettings.itemsPerPage;
    const filterValues = state.filterValues ?? {};
    const modifiedFilters = this.inputs.queryModifier
      ? this.inputs.queryModifier(filterValues as QueryFilterObject)
      : filterValues;
    const smartSearch = extractSmartSearch(modifiedFilters);

    const controller = new AbortController();
    this.deltaAbortControllers.add(controller);

    // Predefined identifiers are always required by the API; include them alongside
    // the delta columns.
    const columns = [...new Set([...addedColumns, ...this.inputs.predefinedColumns])];

    // Cell-level loading: GridCell renders a skeleton for these keys until the
    // delta settles. Only the genuinely added columns — predefined identifiers
    // are already visible and must not shimmer.
    this.deps.store.addPendingColumns(addedColumns);

    try {
      const response = await this.inputs.http.getDataByQuery(
        queryId,
        fetchOffset,
        limit,
        !!smartSearch,
        columns,
        controller.signal
      );

      if (controller.signal.aborted) {
        return;
      }

      // Staleness guards. A filter/preset change while the delta was in flight
      // mints a new queryId and refetches the rows (already including this
      // column) — merging the superseded response would overwrite those fresh
      // cells with values computed for the OLD query. Pagination keeps the
      // queryId but moves the offset: the left-behind page's delta is useless
      // (its row ids no longer render) and its failure fallback must not
      // refetch the OLD offset — that would abort the current page's in-flight
      // fetch and yank the user back. Drop the response in both cases; the
      // current page's own fetch already requests the now-visible column.
      const currentState = this.deps.store.getState();
      const currentOffset =
        currentState.page * (currentState.gridSettings ?? DEFAULT_SETTINGS).itemsPerPage;
      if (currentState.queryId !== queryId || currentOffset !== fetchOffset) {
        return;
      }

      // Delta failure (handleApiError resolves undefined): silently clearing the
      // skeletons would make the column read as "loaded, all empty". Fall back to
      // a full page fetch — it carries the column (now visible) and, if it fails
      // too, surfaces the standard error state with retry.
      if (!response) {
        this.fetchPage(fetchOffset);
        return;
      }

      const patchByRowId = new Map<string, Partial<RowData>>();
      for (const responseRow of response) {
        const patch: Record<string, unknown> = {};
        for (const key of addedColumns) {
          // eslint-disable-next-line max-depth -- Pre-existing violation
          if (responseRow.cells && key in responseRow.cells) {
            patch[key] = responseRow.cells[key];
          }
        }
        patchByRowId.set(responseRow.id, patch as Partial<RowData>);
      }

      this.deps.store.mergeRowCells(patchByRowId);
    } catch (error) {
      // A canceled delta (dispose aborting deltaAbortControllers) rejects with
      // RequestCancelledError: not a failure — the finally clears the skeletons.
      if (!(error instanceof RequestCancelledError)) {
        throw error;
      }
    } finally {
      this.deltaAbortControllers.delete(controller);
      this.deps.store.removePendingColumns(addedColumns);
    }
  }

  /**
   * True when the active preset changed since the snapshot. TWO conditions, both
   * load-bearing:
   * - snapshot.presetJson === null  → first fetch; always a change.
   * - dataQuery !== snapshot.dataQuery → preset activation/deactivation/reset/cold-load
   *   (setDataQuery is only called by those flows), including RE-ACTIVATION of the same
   *   preset ("modify sort -> Reset", commit d82ecd6e02).
   *
   * Deliberately does NOT compare presetJson CONTENT: the active preset JSON is rebuilt
   * from LIVE local state (gridSettings + column widths/visibility/pinning), so a content
   * diff fires on purely presentational changes — rows-per-page, column resize/hide — and
   * would route the fetch to the preset's original dataQueryId, dropping a live
   * filter-minted query and corrupting the persisted preset (T20-130744). Every genuine
   * preset transition replaces the dataQuery OBJECT, so the identity check is sufficient.
   */
  private hasPresetChangedSinceSnapshot(
    snapshot: FetchStateSnapshot,
    dataQuery: GetDataQueryResponse | null
  ): boolean {
    return snapshot.presetJson === null || snapshot.dataQuery !== dataQuery;
  }

  /**
   * True when the filter/sort comparison differs from the snapshot. presetJson handling is
   * separate (see hasPresetChangedSinceSnapshot) — this only compares the smartSearch +
   * filters + sortBy JSON. null snapshot (first fetch) counts as changed.
   */
  private hasFiltersChangedSinceSnapshot(
    snapshot: FetchStateSnapshot,
    newComparisonJson: string
  ): boolean {
    return snapshot.comparisonJson === null || snapshot.comparisonJson !== newComparisonJson;
  }
}
